'use client';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { Box, Typography, Grid, Button, Card, CardContent, CardActions, Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import SvgIcon from '@mui/material/SvgIcon';
import { Icon } from '@iconify/react';
import LazyLoadWrapper from '@/components/LazyLoadWrapper';
import { PlatformIcon } from '@/components/ConnectedAccountCard';
import PlatformCarousel from '@/components/PlatformCarousel';

function YouTubeIcon(props: any) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M10 15l5.2-3L10 9v6z" />
      <path d="M21.6 7.2a2.6 2.6 0 0 0-1.8-1.8C18 5 12 5 12 5s-6 0-7.8.4A2.6 2.6 0 0 0 2.4 7.2 27.2 27.2 0 0 0 2 12a27.2 27.2 0 0 0 .4 4.8 2.6 2.6 0 0 0 1.8 1.8C6 19 12 19 12 19s6 0 7.8-.4a2.6 2.6 0 0 0 1.8-1.8A27.2 27.2 0 0 0 22 12a27.2 27.2 0 0 0-.4-4.8z" />
    </SvgIcon>
  );
}

function InstagramIcon(props: any) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5zm5 6.5A3.5 3.5 0 1 0 15.5 12 3.5 3.5 0 0 0 12 8.5zm4.8-3a1.2 1.2 0 1 0 1.2 1.2 1.2 1.2 0 0 0-1.2-1.2z" />
    </SvgIcon>
  );
}

function LinkedInIcon(props: any) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M19 3H5a2 2 0 0 0-2 2v14h4v-6H7v-4h2V9c0-2 2-3 4-3s4 1 4 3v2h-2v4h2v6h4V5a2 2 0 0 0-2-2z" />
    </SvgIcon>
  );
}

function TwitterIcon(props: any) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M22 5.9c-.6.3-1.2.5-1.9.6.7-.4 1.2-1 1.4-1.7-.7.4-1.5.7-2.3.9A3.7 3.7 0 0 0 12 8c0 .3 0 .6.1.9A10.5 10.5 0 0 1 3 5.4a3.7 3.7 0 0 0 1.1 5 3.6 3.6 0 0 1-1.7-.5v.1a3.7 3.7 0 0 0 3 3.6 3.7 3.7 0 0 1-1.7.1 3.7 3.7 0 0 0 3.5 2.6A7.5 7.5 0 0 1 2 19.6 10.6 10.6 0 0 0 8.3 21c6.3 0 9.8-5.2 9.8-9.8v-.4A7 7 0 0 0 22 5.9z" />
    </SvgIcon>
  );
}

function FacebookIcon(props: any) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M22 12a10 10 0 1 0-11.5 9.9v-7h-2v-3h2v-2.3c0-2 1.2-3.1 3-3.1.9 0 1.8.1 1.8.1v2h-1c-1 0-1.3.6-1.3 1.2V12h2.3l-.4 3h-1.9v7A10 10 0 0 0 22 12z" />
    </SvgIcon>
  );
}

/** Feature icon wrapper: Iconify icon with MUI primary color and large size. */
function FeatureIcon({ icon }: { icon: string }) {
  return (
    <Box sx={{ color: 'primary.main', display: 'inline-flex', '& svg': { width: 36, height: 36 } }}>
      <Icon icon={icon} width={36} height={36} />
    </Box>
  );
}

function StarsIcon(props: any) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M12 17.3L18.18 21 16.54 13.97 22 9.24 14.81 8.63 12 2 9.19 8.63 2 9.24 7.46 13.97 5.82 21z" />
    </SvgIcon>
  );
}

function FormatQuoteIcon(props: any) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M7 7h4v6H7zM13 7h4v6h-4z" />
    </SvgIcon>
  );
}

function CheckCircleOutlineIcon(props: any) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm-1 14.5l-4.5-4.5 1.4-1.4L11 13.7l6.1-6.1 1.4 1.4L11 16.5z" />
    </SvgIcon>
  );
}

const roiPlaceholder = <div style={{ minHeight: 120 }}>Loading ROI Calculator…</div>;
const userCountPlaceholder = <div style={{ minHeight: 40 }}>Loading User Count…</div>;
const platformSelectorPlaceholder = <div style={{ minHeight: 80 }}>Loading Platform Selector…</div>;

// (Platform carousel rendered below)

const FEATURES = [
  { icon: <FeatureIcon icon="mdi:rocket-launch" />, title: 'Launch Fast', desc: 'Go from concept to campaign-ready AI in minutes, not months. Our intuitive tools let you transform ideas into published content with unprecedented speed.' },
  { icon: <FeatureIcon icon="mdi:robot-outline" />, title: 'Built-in AI Automation', desc: 'Automate content creation, analysis, and optimization without writing a single line of code. Leverage intelligent workflows to amplify your reach and engagement.' },
  { icon: <FeatureIcon icon="mdi:connection" />, title: 'Powerful Integrations', desc: 'Seamlessly connect FloAI Studio with your favorite creative tools and social platforms. Automate posting, manage campaigns, and gather insights all from one centralized hub.' },
  { icon: <FeatureIcon icon="mdi:chart-line" />, title: 'ROI Analytics', desc: 'Track real-time results, boost reach, and continually optimize every campaign with our advanced ROI analytics. See the tangible impact of your efforts.' },
];

const PLANS = [
  {
    title: 'Free',
    price: '$0/month',
    features: [
      '3 social accounts',
      'Basic analytics',
      'Community access',
      '2 free Academy courses',
      'Basic template marketplace access',
    ],
    color: 'primary',
    cta: 'Start for Free',
  },
  {
    title: 'Creator',
    price: '$19/month',
    annualPrice: '$189/year',
    features: [
      '3 social accounts',
      'Advanced analytics',
      'Basic scheduling',
      '5 Academy courses',
      'Full template marketplace access',
      'Email support',
    ],
    color: 'info',
    cta: 'Upgrade to Creator',
  },
  {
    title: 'Pro',
    price: '$49/month',
    annualPrice: '$489/year',
    features: [
      'All social accounts',
      'Advanced analytics & insights',
      'Advanced scheduling',
      'All Academy courses',
      'Premium template marketplace',
      'Early access to new features',
      'Priority support',
    ],
    color: 'secondary',
    cta: 'Start Pro free trial',
    highlight: true,
  },
  {
    title: 'Enterprise',
    price: 'Contact Us',
    features: [
      'Custom integrations',
      'Onboarding support',
      'Dedicated account manager',
      'White-glove migrations',
    ],
    color: 'success',
    cta: 'Contact Sales',
  },
];

export default function Home() {
  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        fontFamily: 'system-ui, sans-serif',
        bgcolor: 'background.default',
      }}
    >
        {/* Hero section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 16px 20px 16px',
            textAlign: 'center',
            maxWidth: 600,
            margin: '0 auto',
            pt: { xs: 5, md: 10 }, // Responsive padding-top
            pb: { xs: 2.5, md: 6 }, // Responsive padding-bottom
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontSize: { xs: '2rem', md: '3rem' }, // Responsive font size
              fontWeight: 700,
              mb: 2,
            }}
          >
            The AI Platform <br /> Designed For Real Creators
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: '1.15rem', md: '1.3rem' }, // Responsive font size
              maxWidth: 340,
              margin: '0 auto 20px auto',
              color: 'text.secondary',
            }}
          >
            Build, automate, and scale your creative workflow with generative AI, multi-channel publishing, and analytics—all from a single, modern dashboard.
          </Typography>
          <Button
            component={Link}
            href="/auth/signup"
            variant="contained"
            sx={(theme) => ({
              background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
              color: theme.palette.primary.contrastText,
              borderRadius: 6,
              fontWeight: 600,
              fontSize: '1.15rem',
              padding: '11px 28px',
              margin: '10px 0',
              textDecoration: 'none',
              display: 'inline-block',
              border: 'none',
              boxShadow: `0 2px 12px ${theme.palette.primary.main}33`,
              transition: 'background 0.15s',
              '&:hover': {
                background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.secondary.dark || theme.palette.primary.dark} 90%)`,
              },
            })}
          >
            Get Started Free
          </Button>
        </Box>
        {/* Platforms row */}
        <Box
          sx={{
            // Styles that were in .platformSection
            // Add specific styles here if needed, otherwise rely on Grid container spacing
          }}
        >
          <Typography align="center" variant="subtitle1" fontWeight={600} color="text.primary" sx={{ mb: 2 }}>
            Publish to all your favorite platforms instantly
          </Typography>
          <Box sx={{ mb: 3 }}>
            <PlatformCarousel />
          </Box>
        </Box>
        {/* Features section */}
        <Box
          sx={{
            // Styles that were in .featuresSection
            // Add specific styles here if needed, otherwise rely on Grid container spacing
          }}
        >
          <Grid container justifyContent="center" alignItems="stretch" sx={{ gap: 3 }}>
            {FEATURES.map((f, i) => (
              <Grid key={i} sx={{ width: { xs: '100%', sm: '50%', md: '25%' }, display: 'flex' }}>
                <Box
                  sx={{
                    p: 3,
                    flex: 1,
                    minHeight: 230,
                    bgcolor: 'background.paper',
                    borderRadius: 3,
                    boxShadow: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    justifyContent: 'center',
                    height: '100%',
                  }}
                >
                  {f.icon}
                  <Typography mt={2} fontWeight={700} variant="subtitle1" color="text.primary">{f.title}</Typography>
                  <Typography mt={1} color="text.secondary">{f.desc}</Typography>
                  <Button variant="outlined" size="small" sx={{ mt: 2 }}>Learn More</Button>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
        {/* ROI calculator (lazy in-view) */}
        <LazyLoadWrapper loader={() => import('@/letsgo/templates/ROICalculator')} exportName="ROICalculator" placeholder={roiPlaceholder} rootMargin="400px" />
        {/* User Count (lazy in-view) */}
        <LazyLoadWrapper loader={() => import('@/letsgo/templates/UserCount')} exportName="UserCount" placeholder={userCountPlaceholder} rootMargin="400px" />
        {/* Platform Selector (lazy in-view) */}
        <LazyLoadWrapper loader={() => import('@/letsgo/templates/PlatformSelector')} exportName="PlatformSelector" placeholder={platformSelectorPlaceholder} rootMargin="400px" />
        {/* Pricing plans row */}
        <Box sx={{ mt: 4 }}>
          <Typography align="center" variant="h2" fontWeight={700} mb={4} color="text.primary">Choose Your Plan</Typography>
          <Grid container justifyContent="center" alignItems="stretch" spacing={4}>
            {PLANS.map((plan, /* _ */) => (
              <Grid key={plan.title} item xs={12} sm={6} sx={{ display: 'flex', width: { xs: '100%', sm: '50%', md: 340 } }}>
                <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2, minHeight: 350, width: '100%', height: '100%', mx: 'auto', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography fontWeight={700} variant="subtitle1" mb={0.5} color="text.primary">
                      {plan.title}
                      {plan.highlight && <Chip label="Most Popular" size="small" color="primary" sx={{ ml: 1 }} />}
                    </Typography>
                    <Typography variant="h4" mb={2} color="text.primary">{plan.price}</Typography>
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                      {plan.features.map((f) => (
                        <li key={f} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}><CheckCircleOutlineIcon color="primary" sx={{ mr: 1, fontSize: '1.1rem' }} /><Typography fontSize={14} color="text.secondary">{f}</Typography></li>
                      ))}
                    </ul>
                  </CardContent>
                  <CardActions
                    sx={
                      plan.title === 'Pro'
                        ? (theme) => ({
                            mt: 1,
                            p: 1,
                            width: '100%',
                            borderRadius: 1,
                            backgroundImage: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                          })
                        : {}
                    }
                  >
                    {plan.title === 'Pro' ? (
                      <Button
                        fullWidth
                        variant="text"
                        component={Link}
                        href="/pricing"
                        sx={(theme) => ({
                          color: `${theme.palette.primary.contrastText} !important`,
                          fontWeight: 800,
                          textTransform: 'none',
                          fontSize: '1.25rem',
                          lineHeight: 1,
                          '& span': { color: `${theme.palette.primary.contrastText} !important` },
                          '&:hover': {
                            color: `${theme.palette.primary.contrastText} !important`,
                            bgcolor: alpha(theme.palette.common.white, 0.12),
                          },
                        })}
                      >
                        <Box component="span" sx={{ color: 'inherit' }}>
                          {plan.cta}
                        </Box>
                      </Button>
                    ) : (
                      <Button
                        fullWidth
                        variant="outlined"
                        component={Link}
                        href="/pricing"
                        sx={(theme) => ({
                          mt: 1,
                          ...(theme.palette.mode === 'light' && {
                            bgcolor: 'background.paper',
                            color: 'text.primary',
                            borderColor: 'divider',
                            '&:hover': {
                              bgcolor: 'action.hover',
                              borderColor: 'text.secondary',
                            },
                          }),
                        })}
                      >
                        {plan.cta}
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
        {/* Testimonials placeholder — seeded/mock reviews removed until real reviews are available */}
        <Box id="testimonials" sx={{ my: 4 }}>
          {/* Intentionally left blank; real testimonials will be loaded from CMS/API when available */}
        </Box>
        {/* Footer with Quick Links dropdown */}
        {/* Footer rendered inside ClientHeaderFooter */}
    </Box>
  );
}
